import { useEffect, useMemo, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  ContentCard,
  ResponsiveFlex,
  Row,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import ReactMarkdown from "react-markdown";
import { dollars, modules, useFeature } from "byzantine";
import {
  useAccount,
  useInstitution,
} from "byzantine/src/dbbl/businessLogic/entities";
import {
  useFetchSwiftAdditionalDetails,
  useRecipient,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import utils from "byzantine/src/utils";
import {
  useCreateInternationalWireForm,
  useEffectiveConversionRate,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import { getInternationalWireRecurringRule } from "byzantine/src/dbbl/businessLogic/modules/internationalWires/InternationalWire.form";
import { countryCodeToFlag } from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import filters from "byzantine/src/filters";
import { catcat as cc } from "cerulean";
import { v4 as uuidv4 } from "uuid";
import { useIsInternationalWireDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useFingerPrintData } from "../../../fingerprint";
import HeaderWithSteps from "../../../HeaderWithSteps";
import Address from "../../../settings/profile_settings/UserSettingsProfilePage/Address";
import { getDisplayedFrequency } from "../../FrequencySelector";
import { getInternationalWireTotalPayments } from "./InternationalWireRecurrenceEndCard";
import styles from "./InternationalWire.module.scss";

const InternationalWireReview = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { getValues: getInternationalWireFormValues, handleCreate } =
    useCreateInternationalWireForm();
  const {
    fromAccountId,
    recipientId,
    amount,
    wireReasonDescription,
    memo,
    note,
    currencyTransferPreference,
    quote,
    recurringRule,
    startDate,
    swiftWireTemplate,
  } = getInternationalWireFormValues();
  const isTemplateWire = !!swiftWireTemplate;
  const { requiresAmount, requiresWireReason, requiresMemo, requiresNote } =
    modules.internationalWires.useConditionalDetails();
  const isReceivePreference = currencyTransferPreference === "receive";
  const { fetchSwiftAdditionalDetails } = useFetchSwiftAdditionalDetails();
  const { fetchSwiftAdditionalDetails: fetchCorrespondentBicDetails } =
    useFetchSwiftAdditionalDetails();
  const { captureData } = useFingerPrintData("swift_wire");
  const fromAccount = useAccount(fromAccountId);
  const recipient = useRecipient(recipientId);
  const institution = useInstitution();
  const [institutionName, setInstitutionName] = useState("");
  const [correspondentInstitutionName, setCorrespondentInstitutionName] =
    useState("");
  const [renameFields, setRenameFields] = useState<{
    [key: string]: string;
  }>({});
  const idempotencyKey = useMemo(() => uuidv4() as UUID, []);
  const isInternationalWireDualApprovalRequired =
    useIsInternationalWireDualApprovalRequired();
  const effectiveConversionRate = useEffectiveConversionRate();
  const isScheduledWire = Boolean(
    getInternationalWireRecurringRule(getInternationalWireFormValues())
  );

  const internationalWireFees = institution?.international_wire_fee || [];

  const fee =
    internationalWireFees?.[0]?.amount !== undefined
      ? `$${internationalWireFees[0].amount}`
      : "";

  const onBack = () => {
    navigate("/transfer/wires/international");
  };

  const { featureEquals } = useFeature();

  const onSubmit = async () => {
    let deviceId = "";
    let deviceRequestId = "";
    if (
      featureEquals("third_party_swift_wire_transaction_evaluations", [
        "report",
        "decision",
      ])
    ) {
      ({ deviceId, deviceRequestId } = await captureData());
    }
    handleCreate(
      idempotencyKey,
      deviceId,
      deviceRequestId,
      () => navigate("/transfer/wires/international/receipt"),
      () => navigate("/transfer/wires/international")
    );
  };

  useEffect(() => {
    const fetchSwiftAdditional = async () => {
      if (recipient?.swift_destination?.bic) {
        const res = await fetchSwiftAdditionalDetails(
          recipient.swift_destination.bic
        );
        setInstitutionName(res?.institution_name || "");
        setRenameFields(res?.rename_fields || {});
      }
    };
    fetchSwiftAdditional();
  }, [recipient, fetchSwiftAdditionalDetails]);

  useEffect(() => {
    const fetchCorrespondentDetails = async () => {
      const correspondentBic =
        recipient?.swift_destination?.correspondent_bank_bic;
      if (correspondentBic) {
        const res = await fetchCorrespondentBicDetails(correspondentBic);
        setCorrespondentInstitutionName(res?.institution_name || "");
      } else {
        setCorrespondentInstitutionName("");
      }
    };
    fetchCorrespondentDetails();
  }, [recipient, fetchCorrespondentBicDetails]);

  useEffect(() => {
    if (!getInternationalWireFormValues("hasFormBeenStarted")) {
      navigate("/transfer/wires", { replace: true });
    } else if (!fromAccount || !recipient) {
      navigate("/transfer/wires/international", { replace: true });
    }
  }, [fromAccount, recipient, navigate, getInternationalWireFormValues]);

  const renderRow = (
    row: [string, string | JSX.Element],
    index: number,
    totalRows: number
  ) => {
    const [heading, content] = row;

    return content ? (
      <div
        key={`row-${heading}__${index}`}
        className={cc({ "margin--bottom--l": index !== totalRows - 1 })}
      >
        <Row>
          <Row.Item shrink>
            <p className="fontColor--secondary">{heading}</p>
          </Row.Item>
          <Row.Item>
            <div className={styles.rowContent}>
              <span className={styles.ellipsisWrapper}>{content}</span>
            </div>
          </Row.Item>
        </Row>
      </div>
    ) : null;
  };

  const buildFxAmountRows = (): [string, string | JSX.Element][] => {
    if (!quote) return [];

    const recipientCurrency = isReceivePreference
      ? quote.specified_currency
      : quote.converted_currency;
    const recipientAmount = isReceivePreference
      ? quote.amount
      : quote.converted_amount || "0";
    const usdAmount = isReceivePreference
      ? quote.converted_amount || "0"
      : quote.amount;
    const usdCurrency = isReceivePreference
      ? quote.converted_currency
      : quote.specified_currency;

    return [
      [
        isReceivePreference
          ? l10n.getString("label-currency-amount-send-exactly")
          : l10n.getString("label-currency-amount-send-estimate"),
        <div className={styles.amountFxHeader}>
          <div>
            {isReceivePreference
              ? utils.currencyFormattedValue(recipientAmount, recipientCurrency)
              : `~${utils.currencyFormattedValue(
                  recipientAmount,
                  recipientCurrency
                )}`}
          </div>
          <span className="currency-amount-input end-content margin--left--xs">
            <div>
              {countryCodeToFlag(recipientCurrency?.substring(0, 2) || "US")}
            </div>
            <div>{recipientCurrency}</div>
          </span>
        </div>,
      ],
      [
        isReceivePreference
          ? l10n.getString("label-currency-amount-send-estimate")
          : l10n.getString("label-currency-amount-send-exactly"),
        <div className={styles.amountFxHeader}>
          <div>{utils.currencyFormattedValue(usdAmount, usdCurrency)}</div>
          <span className="currency-amount-input end-content margin--left--xs">
            <div>{countryCodeToFlag("US")}</div>
            <div>{"USD"}</div>
          </span>
        </div>,
      ],
      [
        l10n.getString(
          "international-wire-form-currency-estimated-exchange-rate"
        ),
        effectiveConversionRate
          ? `1 USD = ${effectiveConversionRate.toFixed(4)} ${
              quote?.converted_currency === "USD"
                ? quote?.specified_currency
                : quote?.settlement_currency
            }`
          : "",
      ],
    ];
  };

  const amountDetailsSection = () => (
    <>
      <h1>{`${filters.currency(dollars(amount), { hasDecimal: true })}`}</h1>
      <div className="margin--top--l margin--bottom--m">
        {l10n.getString("international-wire-fee-disclosure")}
      </div>
    </>
  );

  const amountFxDetailsSection = () => {
    const rows: [string, string | JSX.Element][] = quote
      ? [
          [
            isReceivePreference
              ? l10n.getString("label-currency-amount-send-estimate")
              : l10n.getString("label-currency-amount-send-exactly"),
            <div className={styles.amountFxHeader}>
              <div>
                {isReceivePreference
                  ? utils.currencyFormattedValue(
                      quote.converted_amount || "0",
                      quote.converted_currency
                    )
                  : utils.currencyFormattedValue(
                      quote.amount || "0",
                      quote.specified_currency
                    )}
              </div>
              <span className="currency-amount-input end-content margin--left--xs">
                <div>{countryCodeToFlag("US")}</div>
                <div>{"USD"}</div>
              </span>
            </div>,
          ],
          [
            l10n.getString(
              "international-wire-form-currency-estimated-exchange-rate"
            ),
            effectiveConversionRate
              ? `1 USD = ${effectiveConversionRate.toFixed(4)} ${
                  quote?.converted_currency === "USD"
                    ? quote?.specified_currency
                    : quote?.settlement_currency
                }`
              : "",
          ],
        ]
      : [];
    return (
      <>
        <div className={styles.amountFxHeader}>
          <h1>
            {isReceivePreference
              ? utils.currencyFormattedValue(
                  quote?.amount || "0",
                  quote?.specified_currency || "USD"
                )
              : `~${utils.currencyFormattedValue(
                  quote?.converted_amount || "0",
                  quote?.converted_currency || "USD"
                )}`}
          </h1>
          <span className="currency-amount-input end-content margin--left--m">
            {isReceivePreference ? (
              <>
                <div>
                  {countryCodeToFlag(
                    quote?.specified_currency?.substring(0, 2) || "US"
                  )}
                </div>
                <div>{quote?.specified_currency || "USD"}</div>
              </>
            ) : (
              <>
                <div>
                  {countryCodeToFlag(
                    quote?.converted_currency?.substring(0, 2) || "US"
                  )}
                </div>
                <div>{quote?.converted_currency || "USD"}</div>
              </>
            )}
          </span>
        </div>
        <div className="margin--top--l margin--bottom--xl">
          {rows.map((row, i) => renderRow(row, i, rows.length))}
        </div>
      </>
    );
  };

  const paymentScheduleSection = () => {
    if (!isScheduledWire || !startDate) return null;

    const totalPayments = getInternationalWireTotalPayments(
      recurringRule?.frequency,
      startDate,
      recurringRule?.ends,
      recurringRule?.untilDate,
      recurringRule?.count
    );
    const frequency = recurringRule?.isVisible && recurringRule.frequency
      ? getDisplayedFrequency(recurringRule.frequency, l10n) || ""
      : l10n.getString("frequency-once");
    const sentAmount =
      isReceivePreference && quote
        ? Number(quote.converted_amount || amount)
        : amount;
    const rows: [string, string][] = [
      [l10n.getString("ach-payment-starts"), startDate],
      [l10n.getString("label-frequency"), frequency],
    ];

    if (recurringRule?.ends === "on" && recurringRule.untilDate) {
      rows.push([
        l10n.getString("travel-notice-end-date-input"),
        recurringRule.untilDate,
      ]);
    }
    if (
      (recurringRule?.ends === "on" || recurringRule?.ends === "after") &&
      totalPayments > 0
    ) {
      rows.push([
        l10n.getString("international-wire-review-total-payments", {
          totalPayments,
        }),
        filters.currency(dollars(totalPayments * sentAmount), {
          hasDecimal: true,
        }),
      ]);
    }

    return (
      <div className={styles.reviewCardSection}>
        <h5 className="fontSize--xs fontWeight--bold">
          {l10n.getString("ach-payment-payment-schedule").toUpperCase()}
        </h5>
        <div className="margin--top--l">
          {rows.map((row, i) => renderRow(row, i, rows.length))}
        </div>
      </div>
    );
  };

  const wireDetailsSection = () => {
    const rows: [string, string][] = [
      [
        l10n.getString("label-international-wire-review-from"),
        fromAccount?.name || "",
      ],
      [l10n.getString("label-international-wire-review-fees"), fee],
    ];
    const filteredRows = rows.filter((row) => Boolean(row[1]));
    return filteredRows.map((row, i) => renderRow(row, i, filteredRows.length));
  };

  const recipientDetailsSection = () => {
    const isAccountNumber =
      renameFields?.["account_identifier"] === "Account number" ||
      renameFields?.["account_identifier"] === "IBAN";
    const rows: [string, string | JSX.Element][] = [
      [
        l10n.getString("label-international-wire-review-recipient"),
        recipient?.name || "",
      ],
      [
        l10n.getString("label-international-wire-review-bank"),
        institutionName || "",
      ],
      [
        l10n.getString("label-international-wire-review-beneficiary-bank"),
        recipient?.swift_destination?.metadata?.bank_name || "",
      ],
      [
        l10n.getString("label-international-wire-review-bic"),
        recipient?.swift_destination?.bic || "",
      ],
      [
        l10n.getString("label-international-wire-review-account"),
        isAccountNumber
          ? recipient?.swift_destination?.account_identifier || ""
          : "",
      ],
      [
        l10n.getString("label-international-wire-review-correspondent-bank"),
        correspondentInstitutionName,
      ],
      [
        l10n.getString("label-international-wire-review-address"),
        recipient?.swift_destination?.address ? (
          <Address
            address={recipient?.swift_destination?.address as API.Address}
            showCountryCode
          />
        ) : (
          ""
        ),
      ],
    ];
    const filteredRows = rows.filter((row) => Boolean(row[1]));
    return filteredRows.map((row, i) => renderRow(row, i, filteredRows.length));
  };

  const additionalDetailsSection = () => {
    const rows: [string, string][] = [
      [l10n.getString("label-international-wire-review-memo"), memo || ""],
      [
        l10n.getString("label-international-wire-review-reason"),
        wireReasonDescription || "",
      ],
      [l10n.getString("label-international-wire-review-note"), note || ""],
    ];
    const filteredRows = rows.filter((row) => Boolean(row[1]));
    return (
      <div className={styles.reviewCardSection}>
        <h5 className="fontSize--xs fontWeight--bold">
          {" "}
          {l10n.getString("international-wire-review-additional-details")}
        </h5>
        <div className="margin--top--l">
          {filteredRows.map((row, i) => renderRow(row, i, filteredRows.length))}
        </div>
      </div>
    );
  };

  const paymentDetailsMissingFromTemplateSection = () => {
    const rows: [string, string | JSX.Element][] = [];
    if (requiresAmount) {
      if (quote) {
        rows.push(...buildFxAmountRows());
      } else {
        rows.push([
          l10n.getString("label-amount"),
          filters.currency(dollars(amount), { hasDecimal: true }),
        ]);
      }
    }
    if (requiresWireReason && wireReasonDescription) {
      rows.push([
        l10n.getString("label-international-wire-review-purpose"),
        wireReasonDescription,
      ]);
    }
    if (requiresMemo && memo) {
      rows.push([l10n.getString("label-international-wire-review-memo"), memo]);
    }
    if (requiresNote && note) {
      rows.push([l10n.getString("label-international-wire-review-note"), note]);
    }
    if (rows.length === 0) return null;
    return (
      <ContentCard kind="bordered" radiusSize="l" paddingSize="xl">
        <h5 className="fontSize--xs fontWeight--bold">
          {" "}
          {l10n.getString("international-wire-review-payment-details")}
        </h5>
        <div className="margin--top--l">
          {rows.map((row, i) => renderRow(row, i, rows.length))}
        </div>
      </ContentCard>
    );
  };

  const templateDetailsSection = () => {
    if (!swiftWireTemplate) return null;
    const rows: [string, string | JSX.Element][] = [];
    if (!requiresAmount) {
      if (quote) {
        rows.push(...buildFxAmountRows());
      } else {
        rows.push([
          l10n.getString("label-amount"),
          filters.currency(dollars(amount), { hasDecimal: true }),
        ]);
      }
    }
    rows.push([
      l10n.getString("label-international-wire-review-from"),
      fromAccount?.name || "",
    ]);
    if (!requiresMemo && memo) {
      rows.push([l10n.getString("label-international-wire-review-memo"), memo]);
    }
    if (!requiresWireReason && wireReasonDescription) {
      rows.push([
        l10n.getString("label-international-wire-review-purpose"),
        wireReasonDescription,
      ]);
    }
    if (!requiresNote && note) {
      rows.push([l10n.getString("label-international-wire-review-note"), note]);
    }
    return (
      <div className={cc([styles.reviewCardSection, styles.template])}>
        <h5 className="fontSize--xs fontWeight--bold">
          {" "}
          {swiftWireTemplate.name.toUpperCase()}
        </h5>
        <div className="margin--top--l">
          {rows.map((row, i) => renderRow(row, i, rows.length))}
        </div>
      </div>
    );
  };

  return (
    <ResponsiveFlex gapSize="xl">
      <HeaderWithSteps
        headerText={l10n.getString("header-review-your-wire-transfer")}
        useDisplayFont
      />
      {isTemplateWire ? (
        <>
          {paymentDetailsMissingFromTemplateSection()}
          <ContentCard kind="bordered" radiusSize="l" paddingSize="xl">
            {templateDetailsSection()}
            <div className={cc([styles.reviewCardSection, styles.last])}>
              <h5 className="fontSize--xs fontWeight--bold">
                {" "}
                {l10n.getString("international-wire-review-recipient-details")}
              </h5>
              <div className="margin--top--l">{recipientDetailsSection()}</div>
            </div>
          </ContentCard>
        </>
      ) : (
        <ContentCard kind="bordered" radiusSize="l" paddingSize="xl">
          <p>
            {l10n.getString("international-wire-review-subheader", {
              recipientName: recipient?.name || "",
            })}
            <span className="fontColor--secondary">
              {recipient?.nickname ? ` (${recipient?.nickname})` : ""}
            </span>
          </p>
          {quote ? amountFxDetailsSection() : amountDetailsSection()}
          {paymentScheduleSection()}
          <div className={styles.reviewCardSection}>
            <h5 className="fontSize--xs fontWeight--bold">
              {" "}
              {l10n
                .getString("international-wire-form-subheader")
                .toUpperCase()}
            </h5>
            <div className="margin--top--l">{wireDetailsSection()}</div>
          </div>
          {additionalDetailsSection()}
          <div className={cc([styles.reviewCardSection, styles.last])}>
            <h5 className="fontSize--xs fontWeight--bold">
              {" "}
              {l10n.getString("international-wire-review-recipient-details")}
            </h5>
            <div className="margin--top--l">{recipientDetailsSection()}</div>
          </div>
        </ContentCard>
      )}
      {institution?.swift_additional_info && (
        <Alert kind="info" isDismissable={false} isActive={true}>
          <ReactMarkdown>{institution.swift_additional_info}</ReactMarkdown>
        </Alert>
      )}
      <Row alignItems="center">
        <Row.Item>
          <Button
            label={l10n.getString("button-back")}
            testId="cancel-international-wire"
            kind="negative"
            onClick={onBack}
          />
        </Row.Item>
        <Row.Item shrink>
          <Button
            label={l10n.getString(
              isInternationalWireDualApprovalRequired
                ? "button-submit-approval"
                : "label-international-wire-make-payment"
            )}
            testId="submit-international-wire"
            onClick={onSubmit}
          />
        </Row.Item>
      </Row>
    </ResponsiveFlex>
  );
};

export default InternationalWireReview;
